import { normalizeCurrency } from '~common/utils/currency-normalization/currency-normalization'
import { normalizeDateFromFreeText } from '~common/utils/normalizeDateFromFreeText/normalizeDateFromFreeText'
import type { ParsedInvoiceMetadata, RawInvoiceMetadata } from './types'

export function postProcessLLMInvoiceDataExtraction(
    invoiceText: string,
    metadata: RawInvoiceMetadata,
): ParsedInvoiceMetadata | undefined {
    let extractedData: RawInvoiceMetadata = { ...metadata }

    if (extractedData.amount && typeof extractedData.amount === 'string') {
        extractedData.amount = parseFloat(extractedData.amount as any)
    }

    if (
        extractedData.vatRate != null &&
        typeof extractedData.vatRate === 'string'
    ) {
        extractedData.vatRate = parseFloat(extractedData.vatRate as any)
    }

    if (
        extractedData.vatAmount != null &&
        typeof extractedData.vatAmount === 'string'
    ) {
        extractedData.vatAmount = parseFloat(extractedData.vatAmount as any)
    }

    if (!extractedData.currency) {
        const symbol = extractCurrencySymbolFromText(invoiceText)
        if (symbol) {
            extractedData.currency = symbol
        }
    }

    extractedData.currency = normalizeCurrency(extractedData.currency)

    const normalizedDate = normalizeDateFromFreeText(extractedData.date, {
        country: extractedData.vendorCountry ?? undefined,
        currency: extractedData.currency ?? undefined,
    })
    if (!normalizedDate) {
        return undefined
    }
    const parsedDate = new Date(normalizedDate!)

    if (extractedData.amount) {
        const amount = extractedData.amount
        let vatRate = extractedData.vatRate
        if (vatRate != null && vatRate === 0.19) {
            vatRate = 19
        }
        if (vatRate != null && vatRate === 0.07) {
            vatRate = 7
        }
        extractedData.vatRate = vatRate
        const vatAmount = extractedData.vatAmount

        if (
            vatRate !== null &&
            vatRate !== undefined &&
            vatRate > 0 &&
            vatAmount !== null &&
            vatAmount !== undefined &&
            vatAmount > 0
        ) {
            const netAmount = amount - vatAmount
            const effectiveVatRate = calculateTaxRate(netAmount, vatAmount)

            const variance7 = Math.abs(effectiveVatRate - 7)
            const variance19 = Math.abs(effectiveVatRate - 19)

            if (variance7 <= variance19) {
                extractedData.vatRate = 7
                extractedData.vatAmount = calculateVatAmount(netAmount, 7)
            } else {
                extractedData.vatRate = 19
                extractedData.vatAmount = calculateVatAmount(netAmount, 19)
            }
        } else if (
            vatRate !== null &&
            vatRate !== undefined &&
            (vatAmount === null || vatAmount === undefined)
        ) {
            const netAmount = amount / (1 + (vatRate as number) / 100)
            extractedData.vatAmount = calculateVatAmount(netAmount, vatRate)
        } else if (
            vatAmount !== null &&
            vatAmount !== undefined &&
            (vatRate === null || vatRate === undefined)
        ) {
            const netAmount = amount - vatAmount
            extractedData.vatRate = calculateTaxRate(netAmount, vatAmount)
        }
        if (
            extractedData.netAmount == null &&
            extractedData.vatAmount &&
            extractedData.amount
        ) {
            extractedData.netAmount =
                extractedData.amount - extractedData.vatAmount
        }
    }

    const monetaryKeys = ['amount', 'vatAmount', 'netAmount'] as const
    for (const key of monetaryKeys) {
        const value = extractedData[key]
        if (
            value !== null &&
            value !== undefined &&
            typeof value === 'number'
        ) {
            const stringValue = value.toString()
            const hasDecimalPart = stringValue.includes('.')
            if (hasDecimalPart) {
                const decimalPart = stringValue.split('.')[1]
                const hasNonZeroDecimal =
                    decimalPart && decimalPart.replace(/0/g, '').length > 0
                if (hasNonZeroDecimal) {
                    extractedData[key] = Math.round(value * 100) / 100
                }
            }
        }
    }

    delete extractedData.netAmount

    return {
        ...extractedData,
        date: parsedDate.getTime(),
    }
}

function extractCurrencySymbolFromText(text: string): string | null {
    const symbolsRegex = /[$€£¥₽₩₪₹₴₺₱₫₨]/
    const match = text.match(symbolsRegex)
    return match ? match[0] : null
}

function calculateTaxRate(netAmount: number, vatAmount: number): number {
    if (netAmount === 0) return 0
    return (vatAmount / netAmount) * 100
}

function calculateVatAmount(netAmount: number, vatRate: number): number {
    return netAmount * (vatRate / 100)
}
